import { ref } from "vue";
import { ElMessage } from "element-plus";
import "@fontsource-variable/unbounded";
import store from "@/store";

// État global
const isUnicornMode = ref(false);
const unicornClicks = [];
const CLICKS_REQUIRED = 10;
const TIME_WINDOW = 2000;
const DODGES_BEFORE_SURRENDER = 3;
const GALLOP_POINTS = 5;
const MAX_STARS = 48;
const KONAMI = ["ArrowUp", "ArrowUp", "ArrowDown", "ArrowDown", "ArrowLeft", "ArrowRight", "ArrowLeft", "ArrowRight", "b", "a"];

let konamiIndex = 0;
let caught = 0;
let calm = false;

// Minuteries et éléments vivants
let rainTimer = null;
let gallopTimer = null;
let dodgyObserver = null;
let tagTimeout = null;
// Les boutons marqués sont tenus ici : une vue mise en cache par keep-alive
// sort ses boutons du document, querySelectorAll ne les retrouverait pas.
const tagged = new Set();
let aurora = null;
let hotCard = null;
let pointerFrame = null;
let lastPointer = null;
let lastStar = { x: -999, y: -999 };
let stars = 0;

const STAR_COLORS = ["#d6249f", "#7c3aed", "#0284c7", "#059669", "#d97706"];
const STAR_COLORS_NIGHT = ["#ff8ad8", "#ffe27a", "#7cf5d0", "#7fc8ff", "#b79cff"];
const STAR_GLYPHS = ["✦", "✧", "⋆", "✶"];

const WELCOME = [
    "🦄 Mode licorne activé. Les boutons ont désormais leur libre arbitre.",
    "🌈 Mode licorne activé. Les trunks SIP, eux, restent sérieux.",
    "✨ Mode licorne activé. Les factures restent dues, même holographiques.",
    "🧚 Mode licorne activé. Attrapez les licornes, celle qui galope en vaut cinq."
];

const FAREWELL = [
    "Retour à la grisaille réglementaire.",
    "Les licornes sont rentrées. Le sérieux reprend ses droits.",
    "Paillettes balayées. Bon courage pour la suite."
];

const SURRENDER = [
    "Bon, d'accord…",
    "Vous avez gagné.",
    "Je me rends.",
    "Pfff."
];

const pick = list => list[Math.floor(Math.random() * list.length)];
const random = (min, max) => min + Math.random() * (max - min);

// ========================================
// Son magique
// ========================================

function playSound() {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();

        // Arpège féerique (do-mi-sol-do aigu)
        [523, 659, 784, 1047].forEach((freq, i) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = "sine";
            const start = ctx.currentTime + i * 0.12;
            osc.frequency.setValueAtTime(freq, start);
            gain.gain.setValueAtTime(0.2, start);
            gain.gain.exponentialRampToValueAtTime(0.01, start + 0.4);
            osc.connect(gain).connect(ctx.destination);
            osc.start(start);
            osc.stop(start + 0.4);
        });

        // Bruit de paillettes (highpass)
        const bufferSize = ctx.sampleRate * 0.6;
        const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
        const data = buffer.getChannelData(0);
        for (let i = 0; i < bufferSize; i++) {
            data[i] = (Math.random() * 2 - 1) * 0.1;
        }
        const noise = ctx.createBufferSource();
        noise.buffer = buffer;
        const filter = ctx.createBiquadFilter();
        filter.type = "highpass";
        filter.frequency.value = 8000;
        const noiseGain = ctx.createGain();
        noiseGain.gain.setValueAtTime(0.15, ctx.currentTime + 0.3);
        noiseGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.9);
        noise.connect(filter).connect(noiseGain).connect(ctx.destination);
        noise.start(ctx.currentTime + 0.3);
        noise.stop(ctx.currentTime + 0.9);

        setTimeout(() => ctx.close(), 2000);
    } catch {
        // Web Audio non supporté
    }
}

// ========================================
// Bulles d'annonce et chasse aux licornes
// ========================================

function announce(message, type = "success") {
    ElMessage({ message, type, duration: 3500 });
}

function catchUnicorn(el, points) {
    const rect = el.getBoundingClientRect();
    caught += points;
    el.style.animation = "none";
    el.style.top = `${rect.top}px`;
    el.style.left = `${rect.left}px`;
    el.style.transform = "none";
    el.getBoundingClientRect();
    requestAnimationFrame(() => {
        el.style.transition = "transform 0.4s ease-out, opacity 0.4s ease-out";
        el.style.transform = "scale(2.5) rotate(360deg)";
        el.style.opacity = "0";
    });
    setTimeout(() => el.remove(), 500);
    burst(rect.left + rect.width / 2, rect.top + rect.height / 2, points > 1 ? 18 : 8);

    if (points > 1) {
        announce(`🦄💨 Licorne au galop attrapée ! +${points}. Tableau de chasse : ${caught}`);
        return;
    }
    announce(caught === 1 ? "🦄 Licorne attrapée ! Plus que toutes les autres." : `🦄 Licorne attrapée ! Tableau de chasse : ${caught}`);
}

// ========================================
// Étoiles : traînée du curseur et éclats
// ========================================

function spawnStar(x, y, { dx = random(-14, 14), dy = random(10, 30), size = random(10, 18), life = random(0.5, 0.9) } = {}) {
    if (stars >= MAX_STARS) return;
    stars++;

    const star = document.createElement("span");
    star.className = "unicorn-star";
    star.textContent = pick(STAR_GLYPHS);
    star.style.left = `${x}px`;
    star.style.top = `${y}px`;
    star.style.setProperty("--dx", `${dx}px`);
    star.style.setProperty("--dy", `${dy}px`);
    star.style.setProperty("--size", `${size}px`);
    star.style.setProperty("--life", `${life}s`);
    star.style.setProperty("--c", pick(STAR_COLORS));
    star.style.setProperty("--c2", pick(STAR_COLORS_NIGHT));
    setTimeout(() => {
        star.remove();
        stars--;
    }, life * 1000 + 50);
    document.body.appendChild(star);
}

function burst(x, y, count) {
    for (let i = 0; i < count; i++) {
        const angle = (Math.PI * 2 * i) / count + random(-0.2, 0.2);
        const reach = random(40, 90);
        spawnStar(x, y, { dx: Math.cos(angle) * reach, dy: Math.sin(angle) * reach, size: random(12, 20), life: random(0.5, 0.8) });
    }
}

function handleClickWave(e) {
    if (e.button !== 0) return;

    const ring = document.createElement("div");
    ring.className = "unicorn-ring";
    ring.style.left = `${e.clientX}px`;
    ring.style.top = `${e.clientY}px`;
    ring.addEventListener("animationend", () => ring.remove(), { once: true });
    setTimeout(() => ring.remove(), 800);
    document.body.appendChild(ring);
    burst(e.clientX, e.clientY, 6);
}

// ========================================
// La carte holo : inclinaison et reflet qui suivent le curseur
// ========================================

function releaseCard() {
    if (!hotCard) return;
    hotCard.classList.remove("unicorn-holo-hot");
    ["--rx", "--ry", "--mx", "--my"].forEach(prop => hotCard.style.removeProperty(prop));
    hotCard = null;
}

// Une grande carte (un tableau entier) reste tranquille : y promener le reflet
// recalculerait le style de toutes ses lignes à chaque image.
const HOLO_MAX_HEIGHT = 480;

function tiltCard(card, x, y) {
    const rect = card.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0 || rect.height > HOLO_MAX_HEIGHT) {
        if (card === hotCard) releaseCard();
        return;
    }

    const px = Math.min(1, Math.max(0, (x - rect.left) / rect.width));
    const py = Math.min(1, Math.max(0, (y - rect.top) / rect.height));
    const tilt = Math.min(9, 3200 / rect.width);

    card.classList.add("unicorn-holo-hot");
    card.style.setProperty("--mx", `${px * 100}%`);
    card.style.setProperty("--my", `${py * 100}%`);
    card.style.setProperty("--rx", `${(0.5 - py) * 2 * tilt}deg`);
    card.style.setProperty("--ry", `${(px - 0.5) * 2 * tilt}deg`);
}

function onPointerFrame() {
    pointerFrame = null;
    if (!lastPointer) return;

    const { x, y, target } = lastPointer;
    // Un dialog rendu dans une carte n'est pas la carte : l'incliner le rognerait
    const card = target?.closest?.(".el-overlay, .el-dialog") ? null : target?.closest?.(".el-card") ?? null;
    if (card !== hotCard) releaseCard();
    if (card) {
        hotCard = card;
        tiltCard(card, x, y);
    }

    if (Math.hypot(x - lastStar.x, y - lastStar.y) > 16) {
        lastStar = { x, y };
        spawnStar(x + random(-4, 4), y + random(-4, 4));
    }
}

function handlePointerMove(e) {
    lastPointer = { x: e.clientX, y: e.clientY, target: e.target };
    if (pointerFrame) return;
    pointerFrame = requestAnimationFrame(onPointerFrame);
}

function handlePointerLeave() {
    lastPointer = null;
    releaseCard();
}

// ========================================
// Aurore, balayage d'ouverture
// ========================================

function showAurora() {
    if (aurora) return;
    aurora = document.createElement("div");
    aurora.className = "unicorn-aurora";
    document.body.prepend(aurora);
}

function hideAurora() {
    aurora?.remove();
    aurora = null;
}

function wipe() {
    const sweep = document.createElement("div");
    sweep.className = "unicorn-wipe";
    sweep.addEventListener("animationend", () => sweep.remove(), { once: true });
    setTimeout(() => sweep.remove(), 1500);
    document.body.appendChild(sweep);
}

// ========================================
// Licornes qui tombent, licorne au galop
// ========================================

function dropUnicorn() {
    const el = document.createElement("div");
    el.className = "unicorn-rain-drop";
    el.textContent = "🦄";
    el.title = "Attrapez-la !";
    const duration = random(5, 8);
    el.style.cssText = `
        position: fixed;
        top: -60px;
        left: ${random(4, 92)}vw;
        font-size: ${random(30, 46)}px;
        z-index: 99999;
        animation: unicorn-rain-fall ${duration}s linear forwards;
        --unicorn-rain-drift: ${random(-60, 60)}px;
        --unicorn-rain-spin: ${random(-200, 200)}deg;
    `;
    el.addEventListener("click", () => catchUnicorn(el, 1), { once: true });
    setTimeout(() => el.remove(), (duration + 1) * 1000);
    document.body.appendChild(el);
}

function gallop() {
    const ride = random(5, 7);
    const el = document.createElement("div");
    el.className = "unicorn-gallop";
    el.style.top = `${random(18, 70)}vh`;
    el.style.setProperty("--ride", `${ride}s`);

    const rider = document.createElement("span");
    rider.className = "unicorn-gallop__rider";
    rider.textContent = "🦄";
    const trail = document.createElement("span");
    trail.className = "unicorn-gallop__trail";
    el.append(rider, trail);

    rider.title = `Attrapez-la ! Elle vaut ${GALLOP_POINTS}.`;
    rider.addEventListener("click", () => catchUnicorn(el, GALLOP_POINTS), { once: true });
    el.addEventListener("animationend", e => e.target === el && el.remove());
    setTimeout(() => el.remove(), (ride + 1) * 1000);
    document.body.appendChild(el);
}

function scheduleRain() {
    rainTimer = setTimeout(() => {
        if (!isUnicornMode.value) return;
        if (!document.hidden) dropUnicorn();
        scheduleRain();
    }, random(3500, 7000));
}

function scheduleGallop(delay = random(35000, 60000)) {
    gallopTimer = setTimeout(() => {
        if (!isUnicornMode.value) return;
        if (!document.hidden) gallop();
        scheduleGallop();
    }, delay);
}

function startShow() {
    wipe();
    scheduleGallop(1200);
    scheduleRain();
    document.addEventListener("pointermove", handlePointerMove, { passive: true });
    document.documentElement.addEventListener("pointerleave", handlePointerLeave);
    document.addEventListener("pointerdown", handleClickWave, true);
}

function stopShow() {
    clearTimeout(rainTimer);
    clearTimeout(gallopTimer);
    rainTimer = null;
    gallopTimer = null;
    document.removeEventListener("pointermove", handlePointerMove);
    document.documentElement.removeEventListener("pointerleave", handlePointerLeave);
    document.removeEventListener("pointerdown", handleClickWave, true);
    if (pointerFrame) cancelAnimationFrame(pointerFrame);
    pointerFrame = null;
    lastPointer = null;
    releaseCard();
    document.querySelectorAll(".unicorn-rain-drop, .unicorn-gallop, .unicorn-star, .unicorn-ring, .unicorn-wipe").forEach(el => el.remove());
}

// ========================================
// Boutons farceurs (50% esquivent le curseur, puis se rendent)
// ========================================

function surrender(btn) {
    btn.removeEventListener("mouseenter", handleDodgyHover);
    delete btn.dataset.unicornDodgy;
    btn.style.transition = "transform 0.3s ease-in";
    btn.style.transform = "";
    btn.dataset.unicornTitle = btn.getAttribute("title") ?? "";
    btn.title = pick(SURRENDER);
    btn.style.setProperty("animation", "unicorn-jello 0.8s ease", "important");
    setTimeout(() => btn.style.removeProperty("animation"), 800);
}

function handleDodgyHover(e) {
    const btn = e.currentTarget;
    if (!isUnicornMode.value || !btn.dataset.unicornDodgy) return;

    const dodges = Number(btn.dataset.unicornDodges || 0) + 1;
    btn.dataset.unicornDodges = String(dodges);
    if (dodges > DODGES_BEFORE_SURRENDER) {
        surrender(btn);
        return;
    }

    const rect = btn.getBoundingClientRect();
    const offsetX = (Math.random() > 0.5 ? 1 : -1) * (80 + Math.random() * 120);
    const offsetY = (Math.random() > 0.5 ? 1 : -1) * (30 + Math.random() * 60);

    let newX = Math.max(10, Math.min(rect.left + offsetX, window.innerWidth - rect.width - 10));
    let newY = Math.max(10, Math.min(rect.top + offsetY, window.innerHeight - rect.height - 10));

    btn.style.position = "relative";
    btn.style.transition = "transform 0.2s ease-out";
    btn.style.transform = `translate(${newX - rect.left}px, ${newY - rect.top}px)`;

    setTimeout(() => {
        if (btn.dataset.unicornDodgy) {
            btn.style.transition = "transform 0.5s ease-in";
            btn.style.transform = "";
        }
    }, 1500);
}

function startDodgyButtons() {
    function tagButtons() {
        if (!isUnicornMode.value) return;
        document.querySelectorAll(".el-button:not(.theme-toggle-btn)").forEach(btn => {
            if (btn.dataset.unicornTagged) return;
            btn.dataset.unicornTagged = "1";
            tagged.add(btn);
            if (Math.random() < 0.5) {
                btn.dataset.unicornDodgy = "1";
                btn.addEventListener("mouseenter", handleDodgyHover);
            }
        });
    }

    tagButtons();

    dodgyObserver = new MutationObserver(() => {
        if (!isUnicornMode.value) return;
        clearTimeout(tagTimeout);
        tagTimeout = setTimeout(tagButtons, 300);
    });
    dodgyObserver.observe(document.body, { childList: true, subtree: true });
}

function stopDodgyButtons() {
    if (dodgyObserver) {
        dodgyObserver.disconnect();
        dodgyObserver = null;
    }
    clearTimeout(tagTimeout);
    tagTimeout = null;
    tagged.forEach(btn => {
        btn.removeEventListener("mouseenter", handleDodgyHover);
        delete btn.dataset.unicornDodgy;
        btn.style.position = "";
        btn.style.transition = "";
        btn.style.transform = "";
        btn.style.removeProperty("animation");
        if (btn.dataset.unicornTitle !== undefined) {
            if (btn.dataset.unicornTitle) btn.title = btn.dataset.unicornTitle;
            else btn.removeAttribute("title");
            delete btn.dataset.unicornTitle;
        }
        delete btn.dataset.unicornTagged;
        delete btn.dataset.unicornDodges;
    });
    tagged.clear();
}

// ========================================
// API publique
// ========================================

/**
 * Active le mode licorne. Sous prefers-reduced-motion, seules les couleurs
 * et la typographie changent : rien ne bouge, rien ne s'enfuit.
 * Retourne false pour un utilisateur extranet.
 */
function activate() {
    if (store?.getters?.isExtranet) return false;

    calm = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    caught = 0;
    isUnicornMode.value = true;
    document.documentElement.classList.add("unicorn");
    showAurora();
    playSound();
    announce(pick(WELCOME));
    if (calm) return true;

    startShow();
    startDodgyButtons();
    return true;
}

/**
 * Détecte les clics rapides et active le mode licorne si le seuil est atteint.
 * Retourne true si le mode a été activé.
 */
function detectActivation() {
    const now = Date.now();
    unicornClicks.push(now);

    while (unicornClicks.length > 0 && now - unicornClicks[0] > TIME_WINDOW) {
        unicornClicks.shift();
    }

    if (unicornClicks.length < CLICKS_REQUIRED) return false;

    unicornClicks.length = 0;
    return activate();
}

/**
 * Désactive le mode licorne et nettoie tout.
 */
function deactivate() {
    isUnicornMode.value = false;
    document.documentElement.classList.remove("unicorn");
    stopShow();
    stopDodgyButtons();
    hideAurora();

    const tally = caught > 0 ? ` Bilan : ${caught} point${caught > 1 ? "s" : ""} de chasse.` : "";
    announce(pick(FAREWELL) + tally, "info");
}

/**
 * Retourne true si le mode licorne est actif (ref + DOM).
 */
function isActive() {
    return isUnicornMode.value || document.documentElement.classList.contains("unicorn");
}

// ========================================
// Code Konami : ↑ ↑ ↓ ↓ ← → ← → B A
// ========================================

function handleKonami(e) {
    if (!e.key || e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.target?.closest?.("input, textarea, select, [contenteditable]")) return;

    const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
    if (key === KONAMI[konamiIndex]) {
        konamiIndex++;
    } else {
        konamiIndex = key === KONAMI[0] ? (konamiIndex === 2 ? 2 : 1) : 0;
    }

    if (konamiIndex < KONAMI.length) return;

    konamiIndex = 0;
    if (isActive()) {
        deactivate();
        return;
    }
    activate();
}

if (typeof window !== "undefined") {
    window.addEventListener("keydown", handleKonami);
}

export { isUnicornMode, detectActivation, deactivate, isActive };
